import { createApi } from "@reduxjs/toolkit/query/react";

import { createInfiniteQuery } from "./infiniteQuery";

import { IWorkForceUserEntity, TRole } from "@/interfaces/globals";
import { IPermissions, Notification } from "@/interfaces/interfaces";
import { BaseWithAuth, getWorkspace } from "@/utils/helpers";

export const generalApi = createApi({
  reducerPath: "generalQuery",
  baseQuery: BaseWithAuth(),
  tagTypes: ["admin", "manager", "notification"],
  endpoints: (builder) => ({
    // get permissions data
    getPermissions: builder.query<IPermissions, string>({
      query: () => `/general/permission`,
      providesTags: ["admin", "manager"],
      transformResponse: (response: IPermissions) => response,
    }),
    getNotification: builder.query<GeneralResponse<Notification[]>, string>({
      query: () => `/workspace/${getWorkspace()}/notification`,
      providesTags: ["admin", "manager"],
    }),
    getInfiniteNotifications: createInfiniteQuery<Notification[]>(
      builder,
      `/workspace/${getWorkspace()}/notification`,
      ["notification"],
    ),
    readLatestNotification: builder.mutation<
      Notification[],
      { notificationId: string }
    >({
      query: ({ notificationId }) => ({
        url: `/workspace/${getWorkspace()}/notification/${notificationId}/read`,
        method: "PATCH",
      }),
      invalidatesTags: ["notification"],
    }),
    singleReadNotification: builder.mutation<
      Notification,
      {
        notificationId: string;
        data: {
          isRead: boolean;
        };
      }
    >({
      query: ({ notificationId, data }) => ({
        url: `/workspace/${getWorkspace()}/notification/${notificationId}`,
        method: "PUT",
        body: data,
      }),
      invalidatesTags: ["notification"],
    }),
    getWorkforceUsers: builder.query<
      GeneralResponse<IWorkForceUserEntity[]>,
      {
        roles: TRole[];
        filters?: string;
      }
    >({
      query: ({ roles, filters }) => {
        const params = new URLSearchParams();
        roles.forEach((r) => {
          params.append("role", r);
        });

        return `/workspace/${getWorkspace()}/workforce/user/all?${params}${filters ?? ""}`;
      },
      providesTags: ["admin", "manager"],
    }),
  }),
});

export const {
  useGetPermissionsQuery,
  useLazyGetNotificationQuery,
  useReadLatestNotificationMutation,
  useSingleReadNotificationMutation,
  useGetInfiniteNotificationsInfiniteQuery,
  useGetWorkforceUsersQuery,
} = generalApi;
